import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import { exportQrCode, renderPreview } from './qrCodeEngine';

/** Most codes one batch will build — beyond this the browser stalls for minutes. */
export const MAX_BULK_ROWS = 60;

/**
 * One code per line: `value` or `value, file name`. A CSV header row is dropped,
 * so a spreadsheet export can be pasted straight in.
 */
export function parseBulkInput(text) {
    const lines = String(text || '').split('\n').map((line) => line.trim()).filter(Boolean);
    const rows = [];
    lines.forEach((line, index) => {
        if (index === 0 && /^value\s*,\s*name$/i.test(line)) return;
        const comma = line.indexOf(',');
        const value = comma > 0 ? line.slice(0, comma).trim() : line;
        const name = comma > 0 ? line.slice(comma + 1).trim() : '';
        if (!value) return;
        rows.push({ value, name: name || `qr-${rows.length + 1}` });
    });
    return rows.slice(0, MAX_BULK_ROWS);
}

/** File-system safe name that still allows Bangla characters. */
function safeName(name, index) {
    const cleaned = String(name || '').replace(/[^\wঀ-৿ .-]/g, '_').trim();
    return cleaned || `qr-${index + 1}`;
}

/**
 * Small preview images for the grid, rendered one after another. The full
 * design including the frame is kept, so a thumbnail shows exactly what the ZIP
 * will contain.
 */
export async function renderBulkPreviews(rows, design, size = 220) {
    const out = [];
    for (const row of rows) {
        try {
            // eslint-disable-next-line no-await-in-loop -- sequential keeps the UI responsive
            const preview = await renderPreview(row.value, design, size);
            out.push({ ...row, previewUrl: preview.previewUrl });
        } catch {
            out.push({ ...row, previewUrl: null });
        }
    }
    return out;
}

/**
 * @param {(done:number, total:number) => void} [onProgress]
 * @returns {Promise<Blob>} ZIP of one PNG per row
 */
export async function buildBulkZip(rows, design, size, onProgress) {
    const zip = new JSZip();
    for (let i = 0; i < rows.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop -- one canvas at a time keeps memory flat
        const blob = await exportQrCode(rows[i].value, design, { size, format: 'png' });
        zip.file(`${safeName(rows[i].name, i)}.png`, blob);
        onProgress?.(i + 1, rows.length);
    }
    return zip.generateAsync({ type: 'blob' });
}

/** Print resolution the chosen pixel size is laid out at. */
const PDF_DPI = 300;
const PAGE = { w: 210, h: 297, margin: 10 };
const GAP = 8;
const CAPTION_MM = 6;

/**
 * Contact-sheet PDF. The chosen size drives both the raster *and* the printed
 * size — `size` px at 300 DPI — so the grid fits as many codes per page as that
 * size allows instead of a fixed 3×4.
 */
export async function buildBulkPdf(rows, design, size, onProgress) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const usableW = PAGE.w - PAGE.margin * 2;
    const usableH = PAGE.h - PAGE.margin * 2;

    const side = Math.min((size / PDF_DPI) * 25.4, usableW);
    const cellH = side + CAPTION_MM;
    const cols = Math.max(1, Math.floor((usableW + GAP) / (side + GAP)));
    const rowsPerPage = Math.max(1, Math.floor((usableH + GAP) / (cellH + GAP)));
    const perPage = cols * rowsPerPage;
    // Centre whatever the grid actually uses, so a part-full row is not left-heavy.
    const startX = PAGE.margin + (usableW - (cols * side + (cols - 1) * GAP)) / 2;

    for (let i = 0; i < rows.length; i += 1) {
        const slot = i % perPage;
        if (i > 0 && slot === 0) doc.addPage();
        // eslint-disable-next-line no-await-in-loop -- jsPDF writes pages in order
        const blob = await exportQrCode(rows[i].value, { ...design, frameType: 'none' }, { size, format: 'png' });
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await blobToDataUrl(blob);
        const x = startX + (slot % cols) * (side + GAP);
        const y = PAGE.margin + Math.floor(slot / cols) * (cellH + GAP);
        doc.addImage(dataUrl, 'PNG', x, y, side, side);
        doc.setFontSize(8);
        // jsPDF's built-in fonts are Latin-1 only, so a Bangla name would print
        // as boxes — those rows fall back to their position instead.
        const name = String(rows[i].name);
        const caption = /^[\x20-\x7E]+$/.test(name) ? name.slice(0, 26) : `#${i + 1}`;
        doc.text(caption, x + side / 2, y + side + 4, { align: 'center' });
        onProgress?.(i + 1, rows.length);
    }
    return doc.output('blob');
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
